import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import { usuarioApi } from '@/services/api/usuario'
import { getPlatform, getOrCreateDeviceId } from '@/services/device'
import { rememberMeStorage } from '@/services/rememberMe'

const USUARIO_ROLE = 'usuario'
const INVALID_USUARIO_ROLE_MESSAGE = 'Esta conta não possui acesso ao aplicativo do Usuário Bloop.'

export const useAuthStore = defineStore('auth', () => {
  // Estado
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token'))
  const refreshToken = ref(localStorage.getItem('refresh_token'))
  const permissions = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Computed
  const isAuthenticated = computed(() => !!token.value)
  const userRole = computed(() => user.value?.role)
  const isPasswordRecoveryPath = (path = '') =>
    path.includes('/forgot-password') || path.includes('/reset-password/')

  // Actions
  const login = async (credentials) => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.login(credentials)
      
      if (response.success && response?.data?.token) {
        if (response.data.user?.role !== USUARIO_ROLE) {
          throw new Error(INVALID_USUARIO_ROLE_MESSAGE)
        }

        const email = credentials?.email?.trim?.() || ''

        token.value = response.data.token
        refreshToken.value = response.data.refresh_token
        user.value = response.data.user
        permissions.value = response.data.permissions || []
        if (email) {
          localStorage.setItem('user_email', email)
        }

        if (credentials?.remember && email) {
          rememberMeStorage.save(email)
        } else {
          rememberMeStorage.clear()
        }
        
        // Salvar tokens no localStorage
        localStorage.setItem('auth_token', response.data.token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
        
        // Configurar token no axios
        authService.setAuthToken(response.data.token)

        // Registrar token de push se já existir um salvo localmente
        // Não bloquear o fluxo de login (em alguns WebViews pode "pendurar")
        registerPushToken().catch(() => {})
        
        return response
      } else {
        throw new Error(response?.message || 'Resposta inválida no login')
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.register(userData)
      
      if (response.success && response?.data?.token) {
        token.value = response.data.token
        refreshToken.value = response.data.refresh_token
        user.value = response.data.user
        permissions.value = response.data.permissions || []
        if (userData?.email) {
          localStorage.setItem('user_email', userData.email)
        }
        
        // Salvar tokens no localStorage
        localStorage.setItem('auth_token', response.data.token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
        
        // Configurar token no axios
        authService.setAuthToken(response.data.token)

        // Registrar token de push se já existir um salvo localmente
        // Não bloquear o fluxo de login (em alguns WebViews pode "pendurar")
        registerPushToken().catch(() => {})
        
        return response
      } else {
        const registerError = new Error(response?.message || 'Resposta inválida no registro')
        registerError.errors = response?.errors || {}
        registerError.status = response?.status
        throw registerError
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async (options = {}) => {
    const shouldRedirect = options.redirect !== false
    try {
      loading.value = true
      
      // Chamar API de logout
      await authService.logout()
      
      // Limpar estado local
      user.value = null
      token.value = null
      refreshToken.value = null
      permissions.value = []
      error.value = null
      
      // Remover tokens do localStorage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_email')
      
      // Remover token do axios
      authService.setAuthToken(null)
      
      // Redirecionar para login
      if (typeof window !== 'undefined' && shouldRedirect) {
        const currentPath = window.location.pathname || ''
        if (isPasswordRecoveryPath(currentPath)) {
          console.log('[DeepLinkDebug][AuthStoreJS] logout sem redirect em rota de recuperação', {
            currentPath
          })
          return
        }

        const isMobileApp = import.meta.env.VITE_IS_MOBILE_APP === 'true'
        window.location.href = isMobileApp ? '/usuario/login' : '/login'
      }
      
    } catch (err) {
      console.error('Erro ao fazer logout:', err)
      // Mesmo com erro, limpar estado local
      user.value = null
      token.value = null
      refreshToken.value = null
      permissions.value = []
      error.value = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_email')
      authService.setAuthToken(null)
      
      if (typeof window !== 'undefined' && shouldRedirect) {
        const currentPath = window.location.pathname || ''
        if (isPasswordRecoveryPath(currentPath)) {
          console.log('[DeepLinkDebug][AuthStoreJS] logout(catch) sem redirect em rota de recuperação', {
            currentPath
          })
          return
        }

        const isMobileApp = import.meta.env.VITE_IS_MOBILE_APP === 'true'
        window.location.href = isMobileApp ? '/usuario/login' : '/login'
      }
    } finally {
      loading.value = false
    }
  }

  const fetchUser = async () => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.getUser()
      
      if (response.success) {
        user.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Erro ao buscar dados do usuário')
      }
    } catch (err) {
      error.value = err.message
      // Se não conseguir buscar dados do usuário, fazer logout
      await logout()
      throw err
    } finally {
      loading.value = false
    }
  }

  const refreshAuthToken = async () => {
    if (!refreshToken.value) {
      throw new Error('No refresh token available')
    }
    
    try {
      const response = await authService.refreshToken()
      
      if (response.success) {
        token.value = response.data.token
        localStorage.setItem('auth_token', response.data.token)
        authService.setAuthToken(response.data.token)
        return true
      } else {
        throw new Error(response.message || 'Erro ao renovar token')
      }
    } catch (err) {
      // Se não conseguir renovar token, fazer logout
      await logout()
      throw err
    }
  }

  const checkAuth = async () => {
    if (!token.value) {
      return false
    }

    try {
      // Verificar se o token ainda é válido
      await fetchUser()
      return true
    } catch (err) {
      // Se não conseguir verificar, tentar renovar token
      try {
        await refreshAuthToken()
        return true
      } catch (refreshErr) {
        // Se não conseguir renovar, fazer logout
        await logout()
        return false
      }
    }
  }

  const clearError = () => {
    error.value = null
  }

  // Métodos de permissão
  const hasPermission = (permission) => {
    return permissions.value.includes(permission)
  }

  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => permissions.value.includes(permission))
  }

  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => permissions.value.includes(permission))
  }

  const hasRole = (role) => {
    return user.value?.role === role
  }

  const hasAnyRole = (roles) => {
    return roles.includes(user.value?.role)
  }

  const registerPushToken = async (tokenOverride, deviceIdOverride) => {
    try {
      const fcmToken = tokenOverride || localStorage.getItem('fcm_token')
      if (!fcmToken) return

      const deviceId = deviceIdOverride || getOrCreateDeviceId()
      const email = localStorage.getItem('user_email') || user.value?.email || ''

      await usuarioApi.registerPushToken({
        fcm_token: fcmToken,
        device_id: deviceId,
        platform: getPlatform(),
        device_name: deviceId,
        email
      })
    } catch (err) {
      console.warn('Não foi possível registrar o token de push agora', err)
    }
  }

  // Inicializar store
  const init = async () => {
    if (token.value) {
      authService.setAuthToken(token.value)
      try {
        await fetchUser()
      } catch (err) {
        // Se não conseguir buscar usuário, limpar token
        token.value = null
        localStorage.removeItem('auth_token')
        authService.setAuthToken(null)
      }
    }
  }

  return {
    // Estado
    user,
    token,
    refreshToken,
    permissions,
    loading,
    error,
    
    // Computed
    isAuthenticated,
    userRole,
    
    // Actions
    login,
    register,
    logout,
    fetchUser,
    refreshAuthToken,
    checkAuth,
    clearError,
    init,
    
    // Métodos de permissão
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,

    // Push notifications
    registerPushToken
  }
})
